import { useEffect, useState } from "react";
import axios from "axios";
import { Reveal } from "./Reveal";
import { toast } from "@/components/ui/sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  "Essential Pulse",
  "Concierge Plus",
  "Grand Concierge Suite",
  "Not sure yet",
];

const BUSINESS_TYPES = [
  "Restaurant",
  "HVAC / Home Services",
  "Contractor",
  "Chiropractor / Medical",
  "Salon / Med Spa",
  "Retail / Grocery",
  "Automotive",
  "Other",
];

const GOOGLE_RATINGS = [
  "No reviews yet",
  "Under 2 stars",
  "2–3 stars",
  "3–4 stars",
  "4+ stars",
  "Not sure",
];

const empty = {
  name: "", business_name: "", email: "", phone: "", city: "", state: "",
  business_type: "", google_rating: "", tier: "", message: "", website: "",
};

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, tier: e.detail }));
    window.addEventListener("select-tier", handler);
    return () => window.removeEventListener("select-tier", handler);
  }, []);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.business_name.trim()) {
      toast.error("Please share your name, business name, and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form });
      toast.success("Operations Audit Call request received — we'll be in touch shortly.", {
        description: "Consider it a five-star check-in. Welcome to Beam Concierge.",
      });
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-border py-3 text-base text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-[var(--gold)] transition-colors duration-300";

  const selectCls =
    "w-full bg-transparent border-0 border-b border-border rounded-none px-0 py-3 h-auto text-base focus:ring-0 focus:border-[var(--gold)]";

  return (
    <section id="contact" data-testid="contact-form" className="relative bg-[hsl(35_22%_95%)] py-24 lg:py-32 grain overflow-hidden">
      <div className="beam-glow pointer-events-none absolute inset-0 opacity-60" />
      <div className="container-luxe relative z-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20">
        <div>
          <Reveal>
            <p className="overline flex items-center gap-3">
              <span className="h-px w-8 bg-[var(--gold)]" />
              Contact
            </p>
            <h2 className="mt-6 serif text-[clamp(2rem,4.4vw,3.6rem)] leading-[1.03] tracking-tight text-foreground">
              Request your <span className="italic text-gold">operations audit call.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
              Tell us about your business and your current reputation. We'll identify
              the gaps and recommend the right approach — no pressure, just clarity.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 space-y-4 border-t border-border pt-8">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</p>
                <a
                  href="mailto:Contactus@beamconcierge.com"
                  data-testid="contact-email-link"
                  className="serif text-xl text-foreground link-underline"
                >
                  Contactus@beamconcierge.com
                </a>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Based In</p>
                <p className="serif text-xl text-foreground">Charlotte, NC — serving the U.S.</p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="bg-background border border-border p-8 lg:p-10">
            {/* Honeypot: hidden from real users, catches bots */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
              <label>Website
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={update("website")}
                  data-testid="contact-hp-website"
                />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2">Full Name</label>
                <input data-testid="contact-input-name" value={form.name} onChange={update("name")} placeholder="Your full name" className={inputCls} />
              </div>
              <div>
                <label className="overline block mb-2">Business Name</label>
                <input data-testid="contact-input-business" value={form.business_name} onChange={update("business_name")} placeholder="Your business" className={inputCls} />
              </div>
              <div>
                <label className="overline block mb-2">Email</label>
                <input data-testid="contact-input-email" type="email" value={form.email} onChange={update("email")} placeholder="you@business.com" className={inputCls} />
              </div>
              <div>
                <label className="overline block mb-2">Phone</label>
                <input data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} placeholder="(000) 000-0000" className={inputCls} />
              </div>
              <div>
                <label className="overline block mb-2">City</label>
                <input data-testid="contact-input-city" value={form.city} onChange={update("city")} placeholder="Charlotte" className={inputCls} />
              </div>
              <div>
                <label className="overline block mb-2">State</label>
                <input data-testid="contact-input-state" value={form.state} onChange={update("state")} placeholder="NC" className={inputCls} />
              </div>
              <div>
                <label className="overline block mb-2">Business Type</label>
                <Select value={form.business_type} onValueChange={update("business_type")}>
                  <SelectTrigger data-testid="contact-select-business-type" className={selectCls}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`business-type-option-${t}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="overline block mb-2">Current Google Rating</label>
                <Select value={form.google_rating} onValueChange={update("google_rating")}>
                  <SelectTrigger data-testid="contact-select-rating" className={selectCls}>
                    <SelectValue placeholder="Select rating" />
                  </SelectTrigger>
                  <SelectContent>
                    {GOOGLE_RATINGS.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`rating-option-${t}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-6">
              <label className="overline block mb-2">Tier of Interest</label>
              <Select value={form.tier} onValueChange={update("tier")}>
                <SelectTrigger data-testid="contact-select-tier" className={selectCls}>
                  <SelectValue placeholder="Select a tier" />
                </SelectTrigger>
                <SelectContent>
                  {TIERS.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`tier-option-${t}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-6">
              <label className="overline block mb-2">Anything else? <span className="normal-case tracking-normal text-muted-foreground/70">(optional)</span></label>
              <textarea
                data-testid="contact-input-message"
                value={form.message}
                onChange={update("message")}
                rows={3}
                placeholder="Reviews, rankings, or anything you'd like to improve…"
                className={`${inputCls} resize-none`}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="group mt-9 inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-secondary px-8 py-4 text-sm text-secondary-foreground hover:bg-[var(--gold)] transition-colors duration-400 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send Inquiry
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
